// Cloudflare Image Transformations for guest-facing photos.
//
// Photos live in R2 behind an in-zone assets host — assets.ollio.place in
// prod, staging-assets.ollio.place on staging (OH-116). The transformation
// must be requested FROM that same host: Cloudflare's default source policy
// only permits originals on the same domain as the transformation request,
// so routing this through the apex would be rejected.

/** Widths offered to the browser for content images. */
export const LADDER = [320, 640, 960, 1440, 1920] as const;

/**
 * Widest rung, used as the plain `src` alongside a srcset. Declared as its own
 * constant rather than indexed off LADDER: `noUncheckedIndexedAccess` makes
 * every index access possibly-undefined, and the non-null assertion that
 * papers over it is banned by this repo's lint rules.
 */
export const MAX_WIDTH = 1920;

/** Gallery thumbnails render at 96 CSS px; 192 covers a 2x display. */
export const THUMB_WIDTH = 192;

const PREFIX = '/cdn-cgi/image/';

// quality=80 is the ticket's choice. onerror=redirect matters: once the
// monthly free quota is spent Cloudflare answers new transformations with a
// 9422 error, and without this the guest would get a broken image instead of
// the (large but correct) original.
const options = (width: number) =>
  `width=${width},format=auto,quality=80,onerror=redirect`;

function isAbsolute(url: string): boolean {
  return /^https?:\/\//i.test(url);
}

/**
 * Whether Cloudflare Image Transformations are known to exist in front of
 * this host. The `/cdn-cgi/image/…` endpoint is a per-ZONE feature (Images →
 * Transformations, toggled by hand in the dashboard — see docs/18), not a
 * property of "is this an absolute URL". A host outside the `ollio.place`
 * zone — a bare R2 `pub-*.r2.dev` managed domain, or any future third-party
 * host — has no such endpoint at all: requesting it doesn't degrade
 * gracefully like a spent quota does (that's `onerror=redirect`'s job), it
 * 404s outright, turning every photo into a wasted round-trip before the
 * original loads. So this is a decision about WHERE the feature exists, not
 * about which hosts we trust.
 *
 * Both environments now serve photos from inside the zone —
 * `assets.ollio.place` and `staging-assets.ollio.place` (OH-116) — so the
 * suffix test covers staging too and the transformation path is exercised
 * before production rather than only in it.
 */
function hasTransformations(hostname: string): boolean {
  return hostname === 'ollio.place' || hostname.endsWith('.ollio.place');
}

/**
 * Parses `rawUrl` and returns it only if `transformedSrc` would actually
 * rewrite it: absolute, not already wearing the transformation prefix, and
 * on a host where Transformations exist. Shared by `transformedSrc` and
 * `transformedSrcSet` so every bail-out reason (dev-relative,
 * already-transformed, out-of-zone host) is defined exactly once — and
 * `transformedSrcSet` can't emit a srcset with one URL repeated under every
 * rung width for a URL `transformedSrc` wasn't going to touch anyway.
 */
function parseTransformable(rawUrl: string): URL | undefined {
  if (!isAbsolute(rawUrl)) return undefined;

  let parsed: URL;
  try {
    parsed = new URL(rawUrl);
  } catch {
    return undefined;
  }
  if (parsed.pathname.startsWith(PREFIX)) return undefined;
  if (!hasTransformations(parsed.hostname)) return undefined;
  return parsed;
}

/**
 * Rewrites an absolute photo URL into a Cloudflare transformation URL of the
 * given width. Root-relative URLs (the dev shape, no edge in front),
 * already-transformed URLs, and hosts outside the `ollio.place` zone (no
 * Transformations endpoint to call) are returned untouched.
 */
export function transformedSrc(rawUrl: string, width: number): string {
  const parsed = parseTransformable(rawUrl);
  if (!parsed) return rawUrl;

  return `${parsed.origin}${PREFIX}${options(width)}${parsed.pathname}${parsed.search}`;
}

/**
 * Builds a `srcset` across the ladder. Empty for URLs that cannot be
 * transformed, so callers can omit the attribute entirely rather than ship a
 * srcset full of unusable URLs.
 */
export function transformedSrcSet(
  rawUrl: string,
  widths: readonly number[] = LADDER,
): string {
  if (!parseTransformable(rawUrl)) return '';
  return widths.map((w) => `${transformedSrc(rawUrl, w)} ${w}w`).join(', ');
}

/**
 * Open Graph card geometry. Messengers lay cards out at roughly 1.91:1, and
 * 1200x630 is both that shape and the size the static default card already
 * declares — so one number means the same thing everywhere.
 */
export const OG_CARD_WIDTH = 1200;
export const OG_CARD_HEIGHT = 630;

/**
 * Builds the card URL for a photo: cropped to the card's shape, not merely
 * narrowed, so that `og:image:width`/`height` describe the bytes actually
 * delivered instead of the original's proportions.
 *
 * Format is pinned to JPEG rather than `auto`. `auto` negotiates on the
 * Accept header, which is right for browsers and wrong here: OG images are
 * fetched by arbitrary scrapers, and one that sends a permissive Accept could
 * be handed AVIF and render nothing at all.
 *
 * Bails out exactly where `transformedSrc` does — a URL this cannot transform
 * comes back untouched, and the caller must then not claim dimensions for it.
 */
export function ogCardSrc(rawUrl: string): string {
  const parsed = parseTransformable(rawUrl);
  if (!parsed) return rawUrl;

  const opts =
    `width=${OG_CARD_WIDTH},height=${OG_CARD_HEIGHT},fit=cover,` +
    `format=jpeg,quality=80,onerror=redirect`;
  return `${parsed.origin}${PREFIX}${opts}${parsed.pathname}${parsed.search}`;
}
